<template>
  <div
    class="text-sm p-lg border-solid border-1 border-round-sm border-secondary flex flex-column gap-sm text-gray-500"
  >
    <!-- Length validation -->
    <div
      v-if="props.policies.minLength"
      :class="{
        'text-green-600': !isDefaultValueComputed && isMinLengthValidComputed,
        'text-red-500 dark:text-red-500': !isDefaultValueComputed && !isMinLengthValidComputed
      }"
    >
      {{ minimumLengthString }}
    </div>

    <!-- UpperCase validation -->
    <div
      v-if="props.policies.minUpperCase"
      :class="{
        'text-green-600': !isDefaultValueComputed && isUpperCaseValidComputed,
        'text-red-500 dark:text-red-500': !isDefaultValueComputed && !isUpperCaseValidComputed
      }"
    >
      {{ minimumUpperCaseString }}
    </div>

    <!-- LowerCase validation -->
    <div
      v-if="props.policies.minLowerCase"
      :class="{
        'text-green-600': !isDefaultValueComputed && isLowerCaseValidComputed,
        'text-red-500 dark:text-red-500': !isDefaultValueComputed && !isLowerCaseValidComputed
      }"
    >
      {{ minimumLowerCaseString }}
    </div>

    <!-- Numbers validation -->
    <div
      v-if="props.policies.minNumbers"
      :class="{
        'text-green-600': !isDefaultValueComputed && isNumbersValidComputed,
        'text-red-500 dark:text-red-500': !isDefaultValueComputed && !isNumbersValidComputed
      }"
    >
      {{ minimumNumbersString }}
    </div>

    <!-- Special characters validation -->
    <div
      v-if="props.policies.minSpecialCharacters"
      :class="{
        'text-green-600': !isDefaultValueComputed && isSpecialCharacterValidComputed,
        'text-red-500 dark:text-red-500': !isDefaultValueComputed && !isSpecialCharacterValidComputed
      }"
    >
      {{ minimumSpecialCharactersString }}
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, PropType, ref, watchEffect} from 'vue';
import {useTranslation} from 'i18next-vue';
import {PasswordPoliciesType} from '@common/types';

// Props
const props = defineProps({
  policies: {
    type: Object as PropType<PasswordPoliciesType>,
    required: true,
  },
});

// Models
const data = defineModel<string>('data', {required: true, default: ''});
const valid = defineModel<boolean>('valid', {required: true, default: false});

// Variables
const {t} = useTranslation();
const validationTriggerred = ref(false);

// Strings
const minimumLengthString = t('commons.password_policy_minimum_length', {value: props.policies.minLength});
const minimumLowerCaseString = t('commons.password_policy_minimum_lowercase', {value: props.policies.minLowerCase});
const minimumUpperCaseString = t('commons.password_policy_minimum_uppercase', {value: props.policies.minUpperCase});
const minimumNumbersString = t('commons.password_policy_minimum_numbers', {value: props.policies.minNumbers});
const minimumSpecialCharactersString = t('commons.password_policy_minimum_special_chars',
  {
    value: props.policies.minNumbers,
    chars: props.policies.allowedSpecialCharacters,
  });

// Computed
const isDefaultValueComputed = computed(() => {
  if (validationTriggerred.value) {
    return false;
  }

  const result = data.value === null || data.value === undefined || data.value.length === 0;
  if (result) {
    return true;
  }

  validationTriggerred.value = true;
  return false;
});

const isMinLengthValidComputed = computed(() => {
  return data.value?.length >= props.policies.minLength;
});

const isMaxLengthValidComputed = computed(() => {
  return (data.value?.length ?? 0) <= props.policies.maxLength;
});

const isLowerCaseValidComputed = computed(() => {
  return (data.value.match(/[a-z]/g) || []).length >= props.policies.minLowerCase;
});

const isUpperCaseValidComputed = computed(() => {
  return (data.value.match(/[A-Z]/g) || []).length >= props.policies.minLowerCase;
});

const isNumbersValidComputed = computed(() => {
  return (data.value.match(/[0-9]/g) || []).length >= props.policies.minNumbers;
});

const isSpecialCharacterValidComputed = computed(() => {
  return data.value
    .split('')
    .some(c => props.policies.allowedSpecialCharacters.includes(c));
});

// Hooks
watchEffect(() => {
  valid.value = 
    (props.policies.minLength > 0 && isMinLengthValidComputed.value) &&
    (props.policies.minNumbers > 0 && isNumbersValidComputed.value) &&
    (props.policies.minLowerCase > 0 && isLowerCaseValidComputed.value) &&
    (props.policies.minUpperCase > 0 && isUpperCaseValidComputed.value) &&
    (props.policies.minSpecialCharacters > 0 && isSpecialCharacterValidComputed.value) &&
    (props.policies.maxLength > 0 && isMaxLengthValidComputed.value);
});
</script>